import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Users,
  ChartLineUp,
  UserMinus,
  TrendUp,
} from "@phosphor-icons/react";
import KpiCard from "@/components/app/KpiCard";
import UnreadCheckinsCard from "@/components/app/dashboard/UnreadCheckinsCard";
import RecentBadgesCard from "@/components/app/dashboard/RecentBadgesCard";
import TodayAppointmentsCard from "@/components/app/dashboard/TodayAppointmentsCard";
import RecentClientsCard from "@/components/app/dashboard/RecentClientsCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [recentBadges, setRecentBadges] = useState([]);
  const navigate = useNavigate();

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/dashboard/stats`);
      setStats(data);
    } catch (err) {
      console.warn("[Dashboard] failed to fetch stats:", err?.message || err);
    }
  }, []);

  const fetchBadges = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/dashboard/recent-badges?limit=6`);
      setRecentBadges(data);
    } catch (err) {
      console.warn("[Dashboard] failed to fetch recent badges:", err?.message || err);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchBadges();
  }, [fetchStats, fetchBadges]);

  const wc = stats?.workout_completion;
  const inactive = stats?.inactive_clients;
  const improving = stats?.improving_clients;

  return (
    <div data-testid="dashboard-page">
      <div className="mb-8">
        <h1
          className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter uppercase text-white"
          style={{ fontFamily: "Barlow Condensed, sans-serif" }}
        >
          DASHBOARD
        </h1>
        <p className="text-zinc-500 text-sm mt-1 tracking-wide">
          Panoramica operativa dei tuoi clienti
        </p>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KpiCard
          index={0}
          icon={Users}
          label="CLIENTI TOTALI"
          value={stats?.total_clients ?? "—"}
          sublabel="In carico"
          delta={0}
          deltaLabel="—"
          accent="#007AFF"
          onClick={() => navigate("/clients")}
          testId="kpi-total-clients"
        />
        <KpiCard
          index={1}
          icon={ChartLineUp}
          label="WORKOUT COMPLETION"
          value={wc?.current_pct ?? 0}
          suffix="%"
          sublabel="Media ultimi 7 giorni"
          delta={
            wc
              ? Math.round(((wc.current_pct ?? 0) - (wc.prev_pct ?? 0)) * 10) / 10
              : 0
          }
          direction="up_is_good"
          accent="#D4FF00"
          onClick={() => navigate("/clients")}
          testId="kpi-workout-completion"
        />
        <KpiCard
          index={2}
          icon={UserMinus}
          label="CLIENTI INATTIVI"
          value={inactive?.count ?? 0}
          sublabel="Nessun aggiornamento da 7+ giorni"
          delta={inactive ? (inactive.count ?? 0) - (inactive.prev_count ?? 0) : 0}
          direction="down_is_good"
          accent="#FF9500"
          onClick={() => navigate("/clients?filter=inactive")}
          testId="kpi-inactive-clients"
        />
        <KpiCard
          index={3}
          icon={TrendUp}
          label="IN MIGLIORAMENTO"
          value={improving?.count ?? 0}
          sublabel="Trend volume +30 giorni"
          delta={improving ? (improving.count ?? 0) - (improving.prev_count ?? 0) : 0}
          direction="up_is_good"
          accent="#34C759"
          onClick={() => navigate("/clients?filter=improving")}
          testId="kpi-improving-clients"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <UnreadCheckinsCard items={stats?.unread_checkins || []} onNavigate={navigate} />
        <RecentBadgesCard badges={recentBadges} onNavigate={navigate} />
        <TodayAppointmentsCard
          appointments={stats?.today_appointments || []}
          onGoToCalendar={() => navigate("/calendar")}
        />
        <RecentClientsCard clients={stats?.recent_clients || []} onNavigate={navigate} />
      </div>
    </div>
  );
}
